import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';
import {
  CaretDownIcon,
  CaretUpIcon,
  DotIcon,
  ChatIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  HardHatIcon,
  MinusIcon,
  SpinnerGapIcon,
  StopIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
  XIcon
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import Button from 'src/components/ui/Button';
import { CountBadge } from 'src/components/ui/CountBadge';
import Tooltip from 'src/components/ui/Tooltip';
import { apiGet, apiPost } from 'src/lib/api/axios';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { useUploadBatchContext } from '../UploadBatchContext';
import { BatchFileState } from '../types';
import { formatBytes, isFailed, isTerminalStatus, statusLabel } from '../utils';

type Job = {
  id: string;
  kind: 'chat_reply' | 'board_report' | 'upload' | 'review';
  status: 'running' | 'succeeded' | 'failed' | 'cancelled';
  title: string;
  resource_id: string | null;
  source_id: string | null;
  error: string | null;
  created_at: string;
  size: number | null;
};

function formatJobWhen(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const daysAgo = Math.round((startOfDay(new Date()).getTime() - startOfDay(when).getTime()) / 86_400_000);
  if (daysAgo === 0) return when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  if (daysAgo === 1) return 'Yesterday';
  return when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const JOB_LABEL: Record<Job['kind'], string> = {
  chat_reply: 'Reply',
  board_report: 'Board report',
  upload: 'Upload',
  review: 'Review'
};

const JOB_ICON: Record<Job['kind'], Icon> = {
  chat_reply: ChatIcon,
  board_report: HardHatIcon,
  upload: UploadSimpleIcon,
  review: ClipboardTextIcon
};

function JobTypeIcon({ job }: { job: Job }) {
  const TypeIcon = JOB_ICON[job.kind];
  return (
    <span title={JOB_LABEL[job.kind]}>
      <TypeIcon size={16} className="shrink-0 text-content-subtle" aria-hidden />
    </span>
  );
}

function jobMeta(job: Job): { parts: string[]; failed: boolean } | null {
  const parts: string[] = [];
  const when = formatJobWhen(job.created_at);
  if (when) parts.push(when);
  if (job.size != null) parts.push(formatBytes(job.size));
  if (job.status === 'failed' || job.status === 'cancelled') {
    parts.push(job.error || (job.status === 'cancelled' ? 'Cancelled' : 'Failed'));
  }
  if (parts.length === 0) return null;
  return { parts, failed: job.status === 'failed' };
}

function MetaLine({ parts, failed }: { parts: string[]; failed: boolean }) {
  return (
    <span
      className={`mt-0.5 flex min-w-0 items-center truncate text-xs ${failed ? 'text-misc-error' : 'text-content-subtlest'}`}
      title={parts.join(' ')}
    >
      {parts.map((part, index) => (
        <span key={`${part}-${index}`} className="inline-flex min-w-0 items-center">
          {index > 0 && <DotIcon size={14} weight="light" className="shrink-0" aria-hidden />}
          <span className="truncate">{part}</span>
        </span>
      ))}
    </span>
  );
}

function JobStatusIcon({ status }: { status: Job['status'] }) {
  if (status === 'running') {
    return <SpinnerGapIcon size={16} className="shrink-0 animate-spin text-content-default" />;
  }
  if (status === 'succeeded') {
    return <CheckCircleIcon size={16} weight="fill" className="shrink-0 text-green-700" />;
  }
  return <WarningCircleIcon size={16} weight="fill" className="shrink-0 text-misc-error" />;
}

const PREVIEW_COUNT = 2;
const TRAY_DEFAULT_CLASS = 'fixed bottom-4 right-4 z-[1100]';

function useTrayDrag() {
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<{
    id: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
  } | null>(null);
  const moved = useRef(false);

  const begin = (event: ReactPointerEvent<HTMLElement>, element: HTMLElement) => {
    const hit = (event.target as HTMLElement).closest('button, a');
    if (hit && hit !== event.currentTarget) return;
    const rect = element.getBoundingClientRect();
    drag.current = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height
    };
    moved.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event: ReactPointerEvent<HTMLElement>) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (Math.hypot(dx, dy) > 4) moved.current = true;
    setPoint(clampToViewport(current.originX + dx, current.originY + dy, current.width, current.height));
  };
  const end = (event: ReactPointerEvent<HTMLElement>) => {
    if (drag.current?.id === event.pointerId) drag.current = null;
  };
  const consumeClick = (event: { preventDefault(): void; stopPropagation(): void }) => {
    if (!moved.current) return;
    moved.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return { point, setPoint, begin, move, end, consumeClick };
}

function clampToViewport(x: number, y: number, width: number, height: number) {
  return {
    x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - width - 8)),
    y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - height - 8))
  };
}
const SUCCESS_HOLD_MS = 30_000;

function desktopNotificationAllowed(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

function notifyCompletion(job: Job) {
  if (typeof document !== 'undefined' && document.hidden && desktopNotificationAllowed()) {
    new Notification(job.title, { body: `${JOB_LABEL[job.kind]} finished` });
  }
}

/** Successes stay until they have been on screen for 30s. A hidden tab does not count. */
function useHeldSuccesses(
  jobs: Job[],
  dismiss: (id: string) => Promise<void>,
  shownIds: RefObject<Set<string>>,
  trayOpen: boolean
) {
  const previousStatus = useRef(new Map<string, Job['status']>());
  const dismissed = useRef(new Set<string>());
  const [holds, setHolds] = useState<Record<string, { elapsed: number; since: number | null }>>({});
  const [now, setNow] = useState(() => Date.now());
  const [foreground, setForeground] = useState(() => typeof document === 'undefined' || !document.hidden);
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useEffect(() => {
    const onVisibility = () => setForeground(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    setHolds((current) => {
      const next: Record<string, { elapsed: number; since: number | null }> = {};
      const t = Date.now();
      for (const job of jobs) {
        if (job.status !== 'succeeded') continue;
        const prev = current[job.id] ?? { elapsed: 0, since: null };
        let elapsed = prev.elapsed;
        let since = prev.since;
        const onScreen = trayOpen && shownIds.current.has(job.id);
        if ((!foreground || !onScreen) && since != null) {
          elapsed += t - since;
          since = null;
        } else if (foreground && onScreen && since == null) {
          since = t;
        }
        next[job.id] = { elapsed, since };
      }
      return next;
    });

    for (const job of jobs) {
      const previous = previousStatus.current.get(job.id);
      if (job.status === 'succeeded' && previous === 'running') {
        notifyCompletion(job);
      }
      previousStatus.current.set(job.id, job.status);
    }
    const ids = new Set(jobs.map((job) => job.id));
    for (const id of [...previousStatus.current.keys()]) {
      if (!ids.has(id)) previousStatus.current.delete(id);
    }
  }, [jobs, foreground, trayOpen, shownIds]);

  const holding = jobs.some((job) => {
    if (job.status !== 'succeeded') return false;
    const hold = holds[job.id];
    if (!hold) return true;
    const visibleFor = hold.elapsed + (hold.since != null ? now - hold.since : 0);
    return visibleFor < SUCCESS_HOLD_MS;
  });
  useEffect(() => {
    if (!holding || !foreground) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [holding, foreground]);

  const expiredKey = jobs
    .filter((job) => {
      const hold = holds[job.id];
      if (job.status !== 'succeeded' || !hold) return false;
      const visibleFor = hold.elapsed + (hold.since != null ? now - hold.since : 0);
      return visibleFor >= SUCCESS_HOLD_MS;
    })
    .map((job) => job.id)
    .join(',');

  useEffect(() => {
    if (!expiredKey) return;
    for (const id of expiredKey.split(',')) {
      if (dismissed.current.has(id)) continue;
      dismissed.current.add(id);
      void dismissRef.current(id);
    }
  }, [expiredKey]);

  const expired = new Set(expiredKey ? expiredKey.split(',') : []);
  return jobs.filter((job) => !expired.has(job.id));
}

function useJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await apiGet<Job[]>('/jobs', {
          meta: { background: true, globalError: false }
        });
        if (!cancelled) setJobs(response.data);
      } catch {
        // Keep the last list if this poll fails.
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [tick]);

  const dismiss = async (id: string) => {
    setJobs((current) => current.filter((job) => job.id !== id));
    try {
      await apiPost(`/jobs/${id}/dismiss`, undefined, { meta: { globalError: false } });
    } catch {
      setTick((value) => value + 1);
    }
  };

  const dismissAll = async (ids: string[]) => {
    const drop = new Set(ids);
    setJobs((current) => current.filter((job) => !drop.has(job.id)));
    try {
      await apiPost('/jobs/dismiss-all', { ids }, { meta: { globalError: false } });
    } catch {
      setTick((value) => value + 1);
    }
  };

  return { jobs, dismiss, dismissAll };
}

function jobHref(job: Job, tenantCode: string | null): string | undefined {
  if (!job.resource_id) return undefined;
  const path =
    job.kind === 'upload'
      ? `/wiki/${job.resource_id}`
      : job.kind === 'review'
        ? `/review/${job.resource_id}`
        : `/chat/${job.resource_id}`;
  return toTenantPath(path, tenantCode);
}

export default function JobsWidget() {
  const trayDrag = useTrayDrag();
  const panelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !trayDrag.point) return;
    const next = clampToViewport(trayDrag.point.x, trayDrag.point.y, panel.offsetWidth, panel.offsetHeight);
    if (next.x !== trayDrag.point.x || next.y !== trayDrag.point.y) trayDrag.setPoint(next);
  });
  const { phase, batchFiles, hubBatchFiles, stopHubFile, dismissBatchFile } = useUploadBatchContext();
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(true);
  const seenAt = useRef(new Map<string, number>());
  const shownJobIds = useRef(new Set<string>());
  const stoppedUntil = useRef(new Map<string, number>());
  const isStopped = (id: string | null | undefined) => {
    if (!id) return false;
    const until = stoppedUntil.current.get(id);
    return until != null && until > Date.now();
  };
  const markStopped = (file: BatchFileState) => {
    const until = Date.now() + 8000;
    stoppedUntil.current.set(file.fileId, until);
    stoppedUntil.current.set(file.key, until);
    stoppedUntil.current.set(file.name, until);
  };
  const { jobs: fetchedJobs, dismiss, dismissAll } = useJobs();
  const jobs = useHeldSuccesses(fetchedJobs, dismiss, shownJobIds, open);
  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);

  const isHubActive = hubBatchFiles.some((file) => !isStopped(file.fileId) && !isStopped(file.key));
  const activeFiles: BatchFileState[] = (isHubActive ? hubBatchFiles : batchFiles).filter(
    (file) => !isStopped(file.fileId) && !isStopped(file.key) && !isStopped(file.name)
  );
  const uploadsActive = isHubActive || phase === 'uploading' || phase === 'tracking';
  const showUploads = uploadsActive && activeFiles.length > 0;
  const listedJobs = (showUploads ? jobs.filter((job) => job.kind !== 'upload') : jobs)
    .filter((job) => job.resource_id == null || !location.pathname.includes(job.resource_id))
    .filter((job) => job.kind !== 'upload' || (!isStopped(job.source_id) && !isStopped(job.title)));

  const uploadFiles = showUploads ? activeFiles : [];
  let stamp = Date.now();
  for (const file of uploadFiles) {
    if (!seenAt.current.has(file.key)) {
      seenAt.current.set(file.key, stamp);
      stamp += 1;
    }
  }
  const tray = [
    ...listedJobs.map((job) => ({ at: new Date(job.created_at).getTime(), job, file: null })),
    ...uploadFiles.map((file) => ({ at: seenAt.current.get(file.key) ?? 0, job: null, file }))
  ].sort((a, b) => b.at - a.at);
  const needsAttention =
    listedJobs.some((job) => job.status === 'running' || job.status === 'failed' || job.status === 'cancelled') ||
    uploadFiles.some((file) => !isTerminalStatus(file.status) || isFailed(file.status) || file.status === 'infected');
  const attentionWas = useRef(needsAttention);
  useEffect(() => {
    if (attentionWas.current && !needsAttention) setExpanded(false);
    attentionWas.current = needsAttention;
  }, [needsAttention]);

  const taskCount = uploadFiles.length + listedJobs.length;
  const hiddenCount = Math.max(0, taskCount - PREVIEW_COUNT);

  if (taskCount === 0) return null;

  if (!open) {
    const chipHasError =
      uploadFiles.some((file) => isFailed(file.status) || file.status === 'infected') ||
      listedJobs.some((job) => job.status === 'failed' || job.status === 'cancelled');
    const chipRunning =
      listedJobs.some((job) => job.status === 'running') || uploadFiles.some((file) => !isTerminalStatus(file.status));
    return (
      <button
        type="button"
        className={`${trayDrag.point ? 'fixed z-[1100]' : TRAY_DEFAULT_CLASS} flex cursor-grab items-center gap-2 rounded-xl border border-border-default bg-white px-4 py-2 text-sm font-medium text-content-default shadow-md touch-none`}
        style={trayDrag.point ? { left: trayDrag.point.x, top: trayDrag.point.y } : undefined}
        onPointerDown={(event) => trayDrag.begin(event, event.currentTarget)}
        onPointerMove={trayDrag.move}
        onPointerUp={trayDrag.end}
        onClick={(event) => {
          trayDrag.consumeClick(event);
          if (event.defaultPrevented) return;
          setOpen(true);
        }}
        aria-label="Show tasks"
      >
        {chipHasError ? (
          <WarningCircleIcon size={16} weight="fill" className="shrink-0 text-misc-error" />
        ) : chipRunning ? (
          <SpinnerGapIcon size={16} className="shrink-0 animate-spin text-content-default" />
        ) : (
          <CheckCircleIcon size={16} weight="fill" className="shrink-0 text-green-700" />
        )}
        Tasks
        <CountBadge count={taskCount} className="!w-auto min-w-4" />
      </button>
    );
  }

  const visibleTray = expanded ? tray : tray.slice(0, PREVIEW_COUNT);
  shownJobIds.current = new Set(visibleTray.flatMap((item) => (item.job ? [item.job.id] : [])));
  const releaseFile = (file: BatchFileState) => {
    const job = jobs.find((item) => item.kind === 'upload' && item.resource_id === file.fileId);
    if (job) void dismiss(job.id);
    if (isHubActive) stopHubFile(file.key);
    else dismissBatchFile(file.key);
  };

  return (
    <>
      <div
        ref={panelRef}
        className={`${trayDrag.point ? 'fixed z-[1100]' : TRAY_DEFAULT_CLASS} w-80 max-w-[calc(100vw-1rem)] rounded-xl shadow-md border border-border-default bg-white overflow-hidden`}
        style={trayDrag.point ? { left: trayDrag.point.x, top: trayDrag.point.y } : undefined}
        role="status"
        aria-live="polite"
        aria-label="Tasks"
      >
        <div
          className="flex cursor-grab items-center gap-2 border-b border-border-default bg-background-default px-4 py-1 touch-none"
          onPointerDown={(event) => trayDrag.begin(event, event.currentTarget.parentElement as HTMLElement)}
          onPointerMove={trayDrag.move}
          onPointerUp={trayDrag.end}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-content-default">
            <span className="truncate">Tasks</span>
            <CountBadge count={taskCount} className="!w-auto min-w-4" />
          </span>
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="sm" icon={MinusIcon} onClick={() => setOpen(false)} aria-label="Minimise" />
            <Button
              variant="ghost"
              size="sm"
              icon={XIcon}
              aria-label="Dismiss all"
              onClick={() => {
                const uploadJobIds = uploadFiles.flatMap((file) => {
                  const job = jobs.find((item) => item.kind === 'upload' && item.resource_id === file.fileId);
                  return job ? [job.id] : [];
                });
                void dismissAll([...listedJobs.map((job) => job.id), ...uploadJobIds]);
                for (const file of uploadFiles) {
                  if (isHubActive) stopHubFile(file.key);
                  else dismissBatchFile(file.key);
                }
              }}
            />
          </div>
        </div>

        <div className={expanded ? 'max-h-[32rem] overflow-y-auto custom-scrollbar-thin' : undefined}>
          {visibleTray.map((item) => {
            if (item.job) {
              const job = item.job;
              const href = jobHref(job, tenantCode);
              const meta = jobMeta(job);
              const row = (
                <>
                  <JobTypeIcon job={job} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs" title={job.title}>
                      {job.title}
                    </span>
                    {meta && <MetaLine parts={meta.parts} failed={meta.failed} />}
                  </span>
                  <JobStatusIcon status={job.status} />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={XIcon}
                    aria-label="Dismiss"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      void dismiss(job.id);
                    }}
                  />
                </>
              );
              const className = 'flex min-w-0 items-center gap-2 border-b border-border-subtle px-4 py-2';
              return href ? (
                <Link
                  key={job.id}
                  to={href}
                  className={`${className} cursor-pointer transition-colors hover:bg-surface-hover`}
                  onClick={() => {
                    if (job.status !== 'running') void dismiss(job.id);
                  }}
                >
                  {row}
                </Link>
              ) : (
                <div key={job.id} className={className}>
                  {row}
                </div>
              );
            }
            const file = item.file;
            if (!file) return null;
            const failed = isFailed(file.status) || file.status === 'infected';
            const running = !isTerminalStatus(file.status);
            const parts = [formatBytes(file.size)];
            if (file.errorMessage) parts.push(file.errorMessage);
            else if (failed) parts.push(statusLabel(file.status));
            const body = (
              <>
                <UploadSimpleIcon size={16} className="shrink-0 text-content-subtle" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs" title={file.name}>
                    {file.name}
                  </span>
                  <MetaLine parts={parts} failed={failed} />
                </span>
                <JobStatusIcon status={running ? 'running' : failed ? 'failed' : 'succeeded'} />
                {running && isHubActive ? (
                  <Tooltip title="Stop upload" placement="top">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={StopIcon}
                      aria-label="Stop upload"
                      title=""
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        markStopped(file);
                        stopHubFile(file.key);
                      }}
                    />
                  </Tooltip>
                ) : (
                  !running && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={XIcon}
                      aria-label="Dismiss"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        releaseFile(file);
                      }}
                    />
                  )
                )}
              </>
            );
            const className = 'flex min-w-0 items-center gap-2 border-b border-border-subtle px-4 py-2';
            if (file.status !== 'ready') {
              return (
                <div key={file.fileId} className={className}>
                  {body}
                </div>
              );
            }
            return (
              <Link
                key={file.fileId}
                to={toTenantPath(`/wiki/${file.fileId}`, tenantCode)}
                onClick={() => releaseFile(file)}
                className={`${className} cursor-pointer transition-colors hover:bg-surface-hover`}
              >
                {body}
              </Link>
            );
          })}
        </div>

        {!expanded && hiddenCount > 0 && (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 border-t border-border-default px-4 py-1.5 text-xs text-content-subtle hover:bg-background-default"
            onClick={() => setExpanded(true)}
          >
            Show {hiddenCount} more
            <CaretDownIcon size={14} aria-hidden />
          </button>
        )}
        {expanded && taskCount > PREVIEW_COUNT && (
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 border-t border-border-default px-4 py-1.5 text-xs text-content-subtle hover:bg-background-default"
            onClick={() => setExpanded(false)}
          >
            Show less
            <CaretUpIcon size={14} aria-hidden />
          </button>
        )}
      </div>
    </>
  );
}
